import { GOOGLE_TRANSLATE_LANGUAGE_CODES } from '../constants/languages';

export const DEFAULT_GOOGLE_TRANSLATE_LANGUAGE = 'en';
export const AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE = 'auto';
export const GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT = 'bid4babes-google-translate-language-change';
export const GOOGLE_TRANSLATE_APPLIED_EVENT = 'bid4babes-google-translate-applied';
export const GOOGLE_TRANSLATE_FAILED_EVENT = 'bid4babes-google-translate-failed';
export const GOOGLE_TRANSLATE_TARGET_STORAGE_KEY = 'googleTranslate.targetLanguage.v1';

const compatibleLanguageCodeMap: Record<string, string> = {
  'es-419': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'es-US': 'es',
  'fr-CA': 'fr',
  'pt-BR': 'pt',
  'pt-PT': 'pt',
  'pa-Arab': 'pa',
  fil: 'tl',
  he: 'iw',
  jv: 'jw',
  zh: 'zh-CN',
};

export type GoogleTranslateCookie = {
  sourceLanguage: string;
  targetLanguage: string;
};

export type GoogleTranslateLanguageChangeDetail = {
  sourceLanguage: string;
  targetLanguage: string;
};

export const resolveGoogleTranslateLanguageCode = (languageCode?: string | null): string => {
  if (!languageCode) {
    return DEFAULT_GOOGLE_TRANSLATE_LANGUAGE;
  }

  const trimmedCode = languageCode.trim();
  if (!trimmedCode) {
    return DEFAULT_GOOGLE_TRANSLATE_LANGUAGE;
  }

  if (GOOGLE_TRANSLATE_LANGUAGE_CODES.includes(trimmedCode)) {
    return trimmedCode;
  }

  if (compatibleLanguageCodeMap[trimmedCode]) {
    return compatibleLanguageCodeMap[trimmedCode];
  }

  const baseCode = trimmedCode.split('-')[0];
  if (GOOGLE_TRANSLATE_LANGUAGE_CODES.includes(baseCode)) {
    return baseCode;
  }

  return DEFAULT_GOOGLE_TRANSLATE_LANGUAGE;
};

export const resolveGoogleTranslateSourceLanguageCode = (languageCode?: string | null): string => {
  if (languageCode?.trim().toLowerCase() === AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE) {
    return AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE;
  }

  return resolveGoogleTranslateLanguageCode(languageCode);
};

export const getGoogleTranslateCookie = (): GoogleTranslateCookie | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/([^/;]+)\/([^;]+)/);

  if (!match) {
    return null;
  }

  return {
    sourceLanguage: resolveGoogleTranslateSourceLanguageCode(decodeURIComponent(match[1])),
    targetLanguage: resolveGoogleTranslateLanguageCode(decodeURIComponent(match[2])),
  };
};

export const getStoredGoogleTranslateTargetLanguage = (): string | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const storedLanguage = window.localStorage.getItem(GOOGLE_TRANSLATE_TARGET_STORAGE_KEY);
    return storedLanguage ? resolveGoogleTranslateLanguageCode(storedLanguage) : null;
  } catch {
    return null;
  }
};

export const setStoredGoogleTranslateTargetLanguage = (targetLanguage: string) => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      GOOGLE_TRANSLATE_TARGET_STORAGE_KEY,
      resolveGoogleTranslateLanguageCode(targetLanguage)
    );
  } catch {
    // localStorage can be unavailable in private browsing or restricted webviews.
  }
};

const getCookieDomainVariants = (): string[] => {
  if (typeof window === 'undefined') {
    return [''];
  }

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const domainVariants = [''];

  for (let i = 0; i < parts.length; i += 1) {
    const domain = parts.slice(i).join('.');
    domainVariants.push(domain);
    domainVariants.push(`.${domain}`);
  }

  return domainVariants;
};

export const clearAllGoogleTranslateCookies = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const paths = ['/', ''];

  getCookieDomainVariants().forEach((domain) => {
    paths.forEach((path) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      const pathPart = path ? `; path=${path}` : '';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC${domainPart}${pathPart}`;
      document.cookie = `googtrans=; max-age=0${domainPart}${pathPart}`;
    });
  });
};

export const setGoogleTranslateCookie = (sourceLanguage: string, targetLanguage: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const source = resolveGoogleTranslateSourceLanguageCode(sourceLanguage);
  const target = resolveGoogleTranslateLanguageCode(targetLanguage);
  const cookieValue = `/${source}/${target}`;

  document.cookie = `googtrans=${cookieValue}; path=/`;

  getCookieDomainVariants()
    .filter(Boolean)
    .forEach((domain) => {
      document.cookie = `googtrans=${cookieValue}; path=/; domain=${domain}`;
    });
};

export const getCurrentGoogleTranslateTargetLanguage = (defaultLanguage?: string | null): string => {
  const cookie = getGoogleTranslateCookie();
  const storedLanguage = getStoredGoogleTranslateTargetLanguage();
  return resolveGoogleTranslateLanguageCode(cookie?.targetLanguage || storedLanguage || defaultLanguage);
};

export const applyGoogleTranslateTargetLanguage = (
  targetLanguage: string,
  sourceLanguage: string = AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE,
): boolean => {
  const target = resolveGoogleTranslateLanguageCode(targetLanguage);
  setStoredGoogleTranslateTargetLanguage(target);
  setGoogleTranslateCookie(sourceLanguage, target);

  if (typeof document === 'undefined') {
    return false;
  }

  const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
  if (!select) {
    return false;
  }

  if (!Array.from(select.options).some((option) => option.value === target)) {
    return false;
  }

  select.value = target;
  select.dispatchEvent(new Event('change'));
  return true;
};

export const requestGoogleTranslateTargetLanguage = (
  targetLanguage: string,
  sourceLanguage: string = AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE,
): boolean => {
  const source = resolveGoogleTranslateSourceLanguageCode(sourceLanguage);
  const target = resolveGoogleTranslateLanguageCode(targetLanguage);
  const applied = applyGoogleTranslateTargetLanguage(target, source);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<GoogleTranslateLanguageChangeDetail>(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, {
        detail: {
          sourceLanguage: source,
          targetLanguage: target,
        },
      })
    );
  }

  return applied;
};
